/* ═══════════════════════════════════════════════════════════════════════
   RYS-CART-V1 · v1.8 — cart drawer + ATC morph (6-Aug-2026)
   v1.8: Shopflo went live 6pm 6-Aug (replaces Shiprocket Checkout).
   Probe now also reports Shopflo scripts + globals; checkout relay
   gains Shopflo trigger candidates ahead of the /checkout fallback.
   v1.7:
   • Wallet offer removed from the drawer entirely (milestone bar, gift
     auto-add, eviction, worth caption) — free shipping is unconditional,
     so the header shows one quiet ✓ FREE SHIPPING row; footer keeps only
     the taxes line. Side effect: no gift line ever reaches checkout, so
     the BXGY-discount dependency (old Gate B) is gone.
   • Charm rail images switched to object-fit:contain in a taller frame
     (tall charm shots were being center-cropped).
   • Boot + checkout-click probes log which Shiprocket/Fastrr scripts
     and checkout triggers exist, to pin their hook precisely.
   v1.6 (Shiprocket Checkout handoff):
   • Drawer CHECKOUT no longer navigates to /checkout directly (that
     bypassed Shiprocket's button hook → native Shopify checkout).
     It relays a click through, in priority order: the native
     [name="checkout"] trigger in the hidden theme drawer → a
     Shiprocket/Fastrr-injected checkout button → raw /checkout
     fallback (identical to old behavior, so never worse).
   v1.5:
   • Mid-state headline simplified to "Add ₹X more for a free RYS
     wallet" ("Free shipping yours —" dropped; the ✓ seal already says it).
   • FREE WALLET seal caption now shows the gift's worth ("worth ₹999",
     from the resolved gift wallet's price) instead of repeating the
     add-₹X amount from the headline; clears when unlocked.
   v1.4:
   • In-place stepper hardened: zones live inside a single flex wrapper
     child, so theme flex-direction/column styles and ::before/::after
     content on ATC buttons can't stack or clip − q + .
   • Subtle "add ₹X" line under the FREE WALLET seal until the
     threshold is reached; clears when unlocked.
   • syncGift evicts a gift line whose price exceeds the ₹1,000 cap
     (legacy Alvero carts self-heal to an eligible wallet).
   v1.3 (Zomato-reference in-place morph + price-capped gift):
   • ATC buttons morph IN PLACE: the button element itself becomes
     − qty + (original label saved/restored, footprint pinned) — no
     second control layered beside it.
   • Free-gift wallet is price-capped: only wallets worth ≤ ₹1,000
     qualify (his "₹999 or ₹1,000" spec); ladder names win ties, then
     priciest eligible. No eligible wallet in stock → no gift added
     (never silently gifts a pricier one).
   v1.2 (Miraggio-referenced minimal redesign):
   • Card/PDP/sticky stepper pinned to the exact footprint of the ATC
     button it replaces (was height:100% → filled whole card block).
   • Drawer header = store logo image cloned from site header, centered;
     wordmark fallback; "CART · n" line removed.
   • Milestone: free-shipping seal at 50% (middle), wallet at end; thread
     tall enough that labels never overlap content; emoji seals → minimal
     ✓ medallions; confetti removed; chip kept, no emoji.
   • Charms rail shows struck compare-at + % OFF.
   • Footer: "Taxes at checkout" gone → single centered line
     "Free shipping across India · Prices inclusive of all taxes".
   v1.1: observer disconnect-during-write, rAF-debounced morph,
   lazy charm/wallet lookups. Core unchanged:
   • Capture-phase interception of every ATC surface; native Prestige
     drawer retired; every control's state = f(cart).
   • Free-wallet ladder Alvero → Pixie → Blushe by live availability,
     real line item (property _rys_gift) for Shopify + iCarry; zeroed at
     checkout by the ₹4,999 automatic discount.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  if (window.__rysCartLoaded) return; window.__rysCartLoaded = true;

  /* ── config ── */
  var CHARM_BAND_AT = 250000;        // paise: < → charms under ₹900, ≥ → premium
  var CHARM_PRICE_SPLIT = 90000;     // paise
  var GIFT_PROP = '_rys_gift';       // legacy: old carts may still carry a gift-tagged line

  var inr = function (p) { return '₹' + Math.round(p / 100).toLocaleString('en-IN'); };
  var state = { cart: null, charms: [], busy: false };

  /* ── styles ── */
  var css = ''
  + '#rys-cd-scrim{position:fixed;inset:0;background:rgba(31,21,18,.45);opacity:0;pointer-events:none;transition:opacity .3s;z-index:9998}'
  + '#rys-cd{position:fixed;top:0;bottom:0;right:0;width:min(430px,92vw);background:#FFFFFF;z-index:9999;transform:translateX(103%);transition:transform .34s cubic-bezier(.22,.9,.3,1);display:flex;flex-direction:column;box-shadow:-18px 0 40px rgba(31,21,18,.22);font-family:inherit}'
  + 'body.rys-cd-open #rys-cd{transform:translateX(0)}body.rys-cd-open #rys-cd-scrim{opacity:1;pointer-events:auto}'
  + '#rys-cd .cdh{position:relative;text-align:center;padding:20px 16px 14px;border-bottom:1px solid #EFE9DF}'
  + '#rys-cd .lg{height:32px;width:auto;display:inline-block;vertical-align:middle}'
  + '#rys-cd .wm{font-family:Georgia,serif;letter-spacing:.42em;font-size:16px;color:#481716;font-weight:600}'
  + '#rys-cd .cdx{position:absolute;right:8px;top:14px;background:none;border:none;font-size:18px;padding:6px 10px;cursor:pointer;color:#1F1512}'
  + '#rys-cd .shiprow{display:flex;align-items:center;justify-content:center;gap:8px;padding:16px 16px 12px;font-size:10px;letter-spacing:.22em;color:#481716;text-transform:uppercase}'
  + '#rys-cd .shiprow .seal{width:20px;height:20px;border-radius:50%;background:#481716;color:#FAF7F2;display:flex;align-items:center;justify-content:center;font-size:10px;flex:none}'
  + '#rys-cd .items{flex:1;overflow-y:auto;padding:2px 16px}'
  + '#rys-cd .it{display:flex;gap:12px;padding:14px 0;border-bottom:1px solid #EFE9DF}'
  + '#rys-cd .it img{width:60px;height:76px;object-fit:cover;border-radius:8px;background:#F6F2EA;flex:none}'
  + '#rys-cd .iin{flex:1;min-width:0}'
  + '#rys-cd .inm{font-family:Georgia,serif;letter-spacing:.1em;font-size:12px;text-transform:uppercase;color:#1F1512}'
  + '#rys-cd .ivr{font-size:11px;color:#A79A8B;margin-top:1px}'
  + '#rys-cd .ipr{font-size:12.5px;color:#7C7166;margin:3px 0 9px}'
  + '#rys-cd .ipr s{margin-right:6px;color:#B8AC9E}#rys-cd .ipr .free{color:#B23A2E;font-weight:600}'
  + '#rys-cd .rys-urg{font-size:10px;letter-spacing:.1em;text-transform:uppercase;margin-top:4px;line-height:1.4}'
  + '#rys-cd .rys-urg-fast{color:#B23A2E}'
  + '#rys-cd .rys-urg-low{color:#8A6D1F}'
  + '#rys-cd .irow{display:flex;align-items:center;gap:14px}'
  + '#rys-cd .rm{background:none;border:none;font-size:11.5px;color:#A79A8B;text-decoration:underline;cursor:pointer;padding:0}'
  + '#rys-cd .empty{text-align:center;color:#A79A8B;font-size:13px;padding:36px 10px}'
  + '#rys-cd .charms{border-top:1px solid #EFE9DF;padding-top:12px}'
  + '#rys-cd .charms h4{font-size:10px;letter-spacing:.26em;color:#481716;padding:2px 16px 10px;text-transform:uppercase;font-weight:600}'
  + '#rys-cd .rail{display:flex;gap:10px;overflow-x:auto;padding:0 16px 14px;scrollbar-width:none}'
  + '#rys-cd .rail::-webkit-scrollbar{display:none}'
  + '#rys-cd .ch{flex:none;width:112px;background:#fff;border:1px solid #EFE9DF;border-radius:10px;padding:8px;text-align:center}'
  + '#rys-cd .ch img{width:100%;height:112px;object-fit:contain;border-radius:7px;background:#F6F2EA;padding:5px;box-sizing:border-box}'
  + '#rys-cd .ch .cn{font-size:10px;letter-spacing:.05em;margin-top:6px;font-family:Georgia,serif;text-transform:uppercase;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:#1F1512}'
  + '#rys-cd .ch .cp{font-size:11px;color:#7C7166;margin-top:2px}'
  + '#rys-cd .ch .cp s{color:#B8AC9E;font-size:10px;margin-left:4px}'
  + '#rys-cd .ch .coff{font-size:9px;color:#B23A2E;letter-spacing:.08em;margin:1px 0 6px;font-weight:600}'
  + '#rys-cd .ch .cnooff{height:7px}'
  + '#rys-cd .ch button{width:26px;height:26px;border-radius:50%;border:1px solid #481716;background:#fff;color:#481716;font-size:15px;line-height:1;cursor:pointer}'
  + '#rys-cd .ch button.in{background:#481716;color:#FAF7F2}'
  + '#rys-cd .cdf{border-top:1px solid #EFE9DF;padding:12px 16px calc(14px + env(safe-area-inset-bottom));background:#fff}'
  + '#rys-cd .nrow{font-size:11.5px;color:#A79A8B;text-align:center;margin-bottom:10px;letter-spacing:.02em}'
  + '#rys-cd .co{width:100%;border:none;border-radius:99px;background:#481716;color:#FAF7F2;padding:15px 0;font-size:13px;letter-spacing:.18em;text-transform:uppercase;cursor:pointer}'
  + '.rys-instep{display:inline-flex !important;flex-direction:row !important;flex-wrap:nowrap !important;align-items:stretch !important;justify-content:center !important;padding:0 !important;gap:0 !important;letter-spacing:normal !important;box-sizing:border-box;overflow:hidden;white-space:nowrap}'
  + '.rys-instep::before,.rys-instep::after{content:none !important;display:none !important}'
  + '.rys-inwrap{display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center;justify-content:space-between;width:100%;height:100%}'
  + '.rys-instep .rys-sz{flex:1;display:flex;align-items:center;justify-content:center;font-size:17px;line-height:1;min-width:34px;cursor:pointer;user-select:none}'
  + '.rys-instep .rys-sq{flex:0 0 auto;display:flex;align-items:center;justify-content:center;min-width:24px;font-weight:600;font-size:14px;line-height:1}'
  + '.rys-step{display:inline-flex;align-items:center;justify-content:space-between;border:1px solid #481716;border-radius:8px;background:#fff;box-sizing:border-box;height:32px;min-width:92px;vertical-align:middle}'
  + '.rys-step button{background:none;border:none;flex:1;min-width:32px;font-size:15px;color:#481716;cursor:pointer;padding:0;line-height:1;height:100%}'
  + '.rys-step .q{font-size:13px;font-weight:600;color:#1F1512;min-width:16px;text-align:center}'
  + 'cart-drawer#cart-drawer{display:none !important}'   /* retire the native Prestige drawer */
  + '@media (prefers-reduced-motion: reduce){#rys-cd,#rys-cd-scrim{transition:none!important}}';
  var st = document.createElement('style'); st.textContent = css; document.head.appendChild(st);

  /* ── drawer markup ── */
  function logoSrc() {
    var img = document.querySelector('.header__logo-image img, .header__logo img, header a[href="/"] img, .header a[href="/"] img, a.header__logo-link img');
    return img ? (img.currentSrc || img.src || '') : '';
  }
  function mount() {
    var scrim = document.createElement('div'); scrim.id = 'rys-cd-scrim'; scrim.addEventListener('click', closeD);
    var d = document.createElement('aside'); d.id = 'rys-cd';
    var lg = logoSrc();
    var head = lg ? '<img class="lg" src="' + lg + '" alt="RYS">' : '<div class="wm">RYS</div>';
    d.innerHTML =
      '<div class="cdh">' + head +
      '<button class="cdx" aria-label="Close cart">✕</button></div>' +
      '<div class="shiprow"><span class="seal">✓</span>Free shipping across India</div>' +
      '<div class="items" id="rys-items"></div>' +
      '<div class="charms" id="rys-charms" hidden><h4 id="rys-ch-h">Complete the look · Charms</h4><div class="rail" id="rys-rail"></div></div>' +
      '<div class="cdf"><div class="nrow">Prices inclusive of all taxes</div>' +
      '<button class="co" id="rys-co">Checkout&nbsp;&nbsp;·&nbsp;&nbsp;<span id="rys-total">₹0</span></button></div>';
    document.body.appendChild(scrim); document.body.appendChild(d);
    d.querySelector('.cdx').addEventListener('click', closeD);
    document.getElementById('rys-co').addEventListener('click', fireCheckout);
  }
  function fireCheckout() {
    probe('checkout-click');
    /* Shiprocket Checkout hooks the theme's checkout button, not the /checkout
       route — so we relay a click through what it listens to instead of
       navigating. Hidden elements still dispatch clicks, so the retired native
       drawer's trigger works. Fallback = old direct navigation (never worse). */
    var candidates = [
      ['shopflo', 'button[class*="shopflo"], [class*="shopflo"] button, button[id*="shopflo"], button[class*="flo-checkout"], [id*="flo-checkout"] button, [data-flo-checkout]'],
      ['native', 'button[name="checkout"], input[name="checkout"]'],
      ['shiprocket', 'button[class*="fastrr"], [class*="fastrr-checkout"], button[class*="shiprocket"], button[id*="fastrr"], [class*="sr-checkout"] button, button[class*="sr-checkout"]']
    ];
    for (var i = 0; i < candidates.length; i++) {
      var el = document.querySelector(candidates[i][1]);
      if (el) {
        try { console.log('[rys-cart] checkout via ' + candidates[i][0]); } catch (e) {}
        try { el.click(); return; } catch (e) {}
      }
    }
    try { console.log('[rys-cart] checkout via direct /checkout'); } catch (e) {}
    window.location.href = '/checkout';
  }
  var lazyDone = false;
  function openD() {
    document.body.classList.add('rys-cd-open');
    if (!lazyDone) { lazyDone = true; rysStockFetch().then(render).catch(function () {}); resolveCharms().then(render).catch(function () {}); }
  }
  function closeD() { document.body.classList.remove('rys-cd-open'); }

  /* ── cart API ── */
  function j(u, opt) {
    /* RYS-STOCK-MSG: Shopify puts the human sentence in the error body.
       The original discarded it and threw a bare status string. */
    return fetch(u, opt).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return null; }).then(function (body) {
          var e = new Error(u + ' ' + r.status);
          e.status = r.status;
          e.shopifyMessage = (body && (body.description || body.message)) || null;
          throw e;
        });
      }
      return r.json();
    });
  }
  function refresh() { return j('/cart.js').then(function (c) { state.cart = c; render(); return c; }); }
/* ── RYS-STOCK-MSG ──────────────────────────────────────────────
     A 422 from /cart/add.js or /cart/change.js means the requested
     quantity exceeds available stock. Shopify's body says so in words;
     we show it instead of failing silently.
     Self-contained toast: v1.7 removed celebrate() and the #rys-chip it
     drove (both lived in the wallet milestone bar), so there is no
     existing surface to reuse. Inline styles keep this independent of
     the theme's CSS assets. */
  function rysToast(text) {
    var el = document.getElementById('rys-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'rys-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.style.cssText = 'position:fixed;left:50%;bottom:124px;transform:translate(-50%,12px);'
        + 'background:#FAF7F2;color:#481716;border:1px solid #481716;'
        + 'font-size:11.5px;letter-spacing:.1em;text-transform:uppercase;font-weight:600;'
        + 'padding:9px 18px;border-radius:99px;z-index:10001;opacity:0;'
        + 'transition:opacity .25s ease,transform .25s ease;pointer-events:none;'
        + 'white-space:nowrap;font-family:inherit;box-shadow:0 8px 22px rgba(31,21,18,.28)';
      document.body.appendChild(el);
    }
    el.textContent = text;
    requestAnimationFrame(function () {
      el.style.opacity = '1'; el.style.transform = 'translate(-50%,0)';
    });
    clearTimeout(el.__rysT);
    el.__rysT = setTimeout(function () {
      el.style.opacity = '0'; el.style.transform = 'translate(-50%,12px)';
    }, 2600);
  }
  function stockMsg(err) {
    if (!err) return;
    if (err.status !== 422) { try { console.warn('[rys-cart]', err); } catch (e) {} return; }
    var m = err.shopifyMessage || '';
    /* "You can only add 2 of that item to your cart" — that number is the
       maximum they can hold, so it doubles as the honest scarcity line. */
    var n = (m.match(/(\d+)/) || [])[1];
    rysToast(n ? 'Only ' + n + ' left' : 'Sold out');
  }

  /* ── RYS-URGENCY ────────────────────────────────────────────────
     Two lines per cart item, read from assets/rys-stock.json:
       velocity  — today's orders for that variant (>= 1)
       scarcity  — units left (<= 2)
     Shopify's public product JSON has no inventory_quantity and order
     counts are not client-side data, so both come from a server-generated
     feed refreshed hourly. */
  var RYS_SCARCE = 2;
  function rysStockFetch() {
    if (window.RYS_STOCK) return Promise.resolve(window.RYS_STOCK);
    var url = window.RYS_STOCK_URL;
    if (!url) { window.RYS_STOCK = {}; return Promise.resolve(window.RYS_STOCK); }
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (d) { window.RYS_STOCK = d || {}; return window.RYS_STOCK; })
      .catch(function () { window.RYS_STOCK = {}; return window.RYS_STOCK; });
  }
  function rysUrgencyHTML(variantId) {
    var s = (window.RYS_STOCK || {})[String(variantId)];
    if (!s) return '';
    var out = '';
    if (s.d >= 1) out += '<div class="rys-urg rys-urg-fast">Selling fast &middot; ' + s.d + ' ordered today</div>';
    if (s.q > 0 && s.q <= RYS_SCARCE) out += '<div class="rys-urg rys-urg-low">Limited stock available</div>';
    return out;
  }

    function addItem(id, qty, props) {
    var body = { items: [{ id: Number(id), quantity: qty || 1 }] };
    if (props) body.items[0].properties = props;
    return j('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function () { document.dispatchEvent(new CustomEvent('cart:refresh')); return refresh(); });
  }
  function changeLine(line, qty) {
    return j('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line: line, quantity: qty }) })
      .then(function () { document.dispatchEvent(new CustomEvent('cart:refresh')); return refresh(); });
  }

  /* ── derived ── */
  function qtyOf(variantId) {
    var it = (state.cart.items || []).find(function (i) { return i.variant_id === Number(variantId) && !(i.properties && i.properties[GIFT_PROP]); });
    return it ? it.quantity : 0;
  }
  function lineOf(variantId) {
    for (var i = 0; i < state.cart.items.length; i++)
      if (state.cart.items[i].variant_id === Number(variantId) && !(state.cart.items[i].properties && state.cart.items[i].properties[GIFT_PROP])) return i + 1;
    return 0;
  }

  /* ── charms ── */
  function resolveCharms() {
    return j('/search/suggest.json?q=charm&resources[type]=product&resources[limit]=10')
      .then(function (r) {
        var prods = (r.resources && r.resources.results && r.resources.results.products) || [];
        return Promise.all(prods.slice(0, 10).map(function (p) {
          return j('/products/' + p.handle + '.js').catch(function () { return null; });
        }));
      })
      .then(function (list) {
        state.charms = (list || []).filter(function (p) { return p && p.available && /charm/i.test(p.title); })
          .map(function (p) {
            var v = (p.variants || []).find(function (v) { return v.available; }) || p.variants[0];
            return { title: p.title.replace(/charm/i, '').trim() || p.title,
                     price: v.price, cap: v.compare_at_price || 0, vid: v.id,
                     img: (p.images && p.images[0]) || '' };
          });
      })
      .catch(function () { state.charms = []; });
  }

  /* ── render ── */
  function render() {
    if (!state.cart) return;
    var c = state.cart, sub = c.items_subtotal_price;
    document.getElementById('rys-total').textContent = inr(c.total_price);
    /* items */
    var host = document.getElementById('rys-items'); host.innerHTML = '';
    c.items.forEach(function (it, idx) {
      var el = document.createElement('div'); el.className = 'it';
      var img = it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/, '_200x$1$2') : '';
      var priceHTML = inr(it.final_line_price);
      var urgHTML = rysUrgencyHTML(it.variant_id);
      var varTitle = (it.variant_title && it.variant_title !== 'Default Title') ? '<div class="ivr">' + it.variant_title + '</div>' : '';
      var controls = '<div class="rys-step" data-line="' + (idx + 1) + '"><button data-d="-1">−</button><span class="q">' + it.quantity + '</span><button data-d="1">+</button></div>' +
        '<button class="rm" data-line="' + (idx + 1) + '">Remove</button>';
      el.innerHTML = '<img src="' + img + '" alt="" loading="lazy"><div class="iin"><div class="inm">' + it.product_title + '</div>' +
        varTitle + '<div class="ipr">' + priceHTML + '</div>' + urgHTML + '<div class="irow">' + controls + '</div></div>';
      host.appendChild(el);
    });
    if (!c.items.length) host.innerHTML = '<div class="empty">Your cart is empty — add a bag to begin.</div>';
    /* charms rail — price band + % off */
    var wrap = document.getElementById('rys-charms'), rail = document.getElementById('rys-rail');
    var band = sub >= CHARM_BAND_AT ? 'premium' : 'cheap';
    var pool = state.charms.filter(function (ch) {
      return band === 'premium' ? ch.price >= CHARM_PRICE_SPLIT : ch.price < CHARM_PRICE_SPLIT;
    });
    if (!pool.length) pool = state.charms;
    wrap.hidden = pool.length === 0 || c.items.length === 0;
    rail.innerHTML = '';
    pool.slice(0, 6).forEach(function (ch) {
      var inCart = qtyOf(ch.vid) > 0;
      var off = (ch.cap && ch.cap > ch.price) ? Math.round((ch.cap - ch.price) / ch.cap * 100) : 0;
      var priceRow = '<div class="cp">' + inr(ch.price) + (off ? '<s>' + inr(ch.cap) + '</s>' : '') + '</div>' +
        (off ? '<div class="coff">' + off + '% OFF</div>' : '<div class="cnooff"></div>');
      var el = document.createElement('div'); el.className = 'ch';
      el.innerHTML = '<img src="' + ch.img + '" alt="" loading="lazy"><div class="cn">' + ch.title + '</div>' +
        priceRow + '<button data-charm="' + ch.vid + '" class="' + (inCart ? 'in' : '') + '">' + (inCart ? '✓' : '+') + '</button>';
      rail.appendChild(el);
    });
    safeMorph();
  }

  /* ── ATC morph: every control's state = f(cart) ── */
  function variantOf(ctrl) {
    if (ctrl.dataset && ctrl.dataset.variantId) return ctrl.dataset.variantId;
    var form = ctrl.closest('form[action*="/cart/add"], form[action*="cart/add"]') || ctrl.closest('product-form form');
    if (form) { var inp = form.querySelector('[name="id"]'); if (inp) return inp.value; }
    return null;
  }
  function morphable() {
    return document.querySelectorAll('.lc-atc, .cc-quick-add, .rys-atc-btn, .product-card__quick-add-button, [data-rys-atc]');
  }
  var mo = null, moScheduled = false;
  function safeMorph() {
    if (moScheduled) return; moScheduled = true;
    requestAnimationFrame(function () {
      moScheduled = false;
      if (mo) mo.disconnect();
      try { morphAll(); } finally {
        if (mo) mo.observe(document.body, { childList: true, subtree: true });
      }
    });
  }
  function morphAll() {
    if (!state.cart) return;
    morphable().forEach(function (btn) {
      var vid = variantOf(btn); if (!vid) return;
      var q = qtyOf(vid);
      if (q > 0) {
        if (btn.__rysMode !== 'step') {
          /* v1.3 Zomato-style: the button ITSELF becomes − q + . Same element,
             same bg/border/position — we save its label, pin its measured
             footprint (zeroed padding must not shrink it), and swap content. */
          var bw = btn.offsetWidth, bh = btn.offsetHeight;
          btn.__rysOrig = btn.innerHTML;
          if (bw) btn.style.width = bw + 'px';
          if (bh) btn.style.height = bh + 'px';
          btn.classList.add('rys-instep');
          btn.innerHTML = '<span class="rys-inwrap"><span class="rys-sz" data-d="-1">−</span><span class="rys-sq">' + q + '</span><span class="rys-sz" data-d="1">+</span></span>';
          btn.__rysMode = 'step';
        } else {
          var qEl = btn.querySelector('.rys-sq');
          if (qEl && qEl.textContent !== String(q)) qEl.textContent = q;
        }
      } else if (btn.__rysMode === 'step') {
        btn.classList.remove('rys-instep');
        btn.style.width = ''; btn.style.height = '';
        btn.innerHTML = btn.__rysOrig || 'Add to cart';
        btn.__rysMode = 'atc';
      }
    });
  }

  /* ── capture-phase interception: old flash handlers + native drawer never fire ── */
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('.lc-atc, .cc-quick-add, .product-card__quick-add-button, .rys-atc-btn, [data-rys-atc]');
    if (t) {
      var vid = variantOf(t); if (!vid) return;
      e.preventDefault(); e.stopImmediatePropagation();
      if (t.__rysMode === 'step') {
        /* button is in − q + mode: only the two zones act; middle is inert */
        var dz = e.target.closest('[data-d]'); if (!dz) return;
        if (state.busy) return; state.busy = true;
        var line = lineOf(vid), nq = qtyOf(vid) + Number(dz.dataset.d);
        var op = line ? changeLine(line, Math.max(0, nq))
                      : (Number(dz.dataset.d) > 0 ? addItem(vid, 1) : Promise.resolve());
        op.catch(stockMsg).then(function () { state.busy = false; });
        return;
      }
      if (state.busy) return; state.busy = true;
      addItem(vid, 1).then(openD).catch(stockMsg).then(function () { state.busy = false; });
      return;
    }
    var cartLink = e.target.closest && e.target.closest('a[href$="/cart"], a[href="/cart"], [data-action="open-cart"], .header__cart, [aria-controls="cart-drawer"]');
    if (cartLink) { e.preventDefault(); e.stopImmediatePropagation(); openD(); }
  }, true);
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f.matches || !f.matches('form[action*="cart/add"]')) return;
    e.preventDefault(); e.stopImmediatePropagation();
    var inp = f.querySelector('[name="id"]'); if (!inp) return;
    var q = f.querySelector('[name="quantity"]');
    if (state.busy) return; state.busy = true;
    addItem(inp.value, q ? Number(q.value) || 1 : 1).then(openD).catch(stockMsg).then(function () { state.busy = false; });
  }, true);
  document.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'id') setTimeout(safeMorph, 50);   /* variant switch re-sync */
  }, true);
  document.addEventListener('click', function (e) {
    var cb = e.target.closest && e.target.closest('#rys-cd [data-charm]');
    if (!cb) return;
    var vid = cb.dataset.charm, q = qtyOf(vid);
    if (q > 0) { var l = lineOf(vid); if (l) changeLine(l, 0); }
    else addItem(vid, 1).catch(stockMsg);
  });
  document.addEventListener('click', function (e) {
    var host = e.target.closest && e.target.closest('#rys-items');
    if (!host) return;
    var stepBtn = e.target.closest('.rys-step button[data-d]');
    if (stepBtn) {
      var line = Number(stepBtn.closest('.rys-step').dataset.line);
      var item = state.cart.items[line - 1];
      changeLine(line, Math.max(0, item.quantity + Number(stepBtn.dataset.d))).catch(stockMsg);
      return;
    }
    var rm = e.target.closest('.rm');
    if (rm) changeLine(Number(rm.dataset.line), 0);
  });

  /* ── boot ── */
  function probe(tag) {
    try {
      var srs = Array.prototype.map.call(
        document.querySelectorAll('script[src*="fastrr"],script[src*="shiprocket"],script[src*="pickrr"],script[src*="shopflo"],script[src*="flo-checkout"],script[src*="flocheckout"]'),
        function (s) { return s.src; });
      console.log('[rys-cart] probe:' + tag, {
        nativeCheckoutBtn: !!document.querySelector('[name="checkout"]'),
        shopfloGlobal: !!(window.Shopflo || window.shopflo || window.floCheckout || window.FloCheckout),
        scripts: srs });
    } catch (e) {}
  }
  function boot() {
    mount();
    setTimeout(function () { probe('boot'); }, 2500);
    refresh().catch(function () {});   /* one request at page load; charms+wallets resolve on first open */
    /* observe DOM for late-rendered cards — disconnected during our own writes, rAF-debounced */
    mo = new MutationObserver(safeMorph);
    mo.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
